import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash, Megaphone, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Modal, Field, Loader, Empty, Badge, ExportButtons, fadeUp } from "../components/kit";

const AUD = [["all", "Everyone"], ["parents", "Parents"], ["students", "Students"], ["teachers", "Teachers"]];

function NoticeForm({ onClose, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState({ title: "", body: "", audience: "all", priority: "normal" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notices", f);
      toast.success("Notice posted");
      onSaved();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={t("Post notice")} testid="notice-form-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("Title")}><input className="field" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="notice-title-input" /></Field>
        <Field label={t("Description")}><textarea rows={5} required className="field" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="notice-body-input" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("Audience")}>
            <select className="field" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} data-testid="notice-audience-select">
              {AUD.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select className="field" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="notice-priority-select">
              <option value="normal">Normal</option><option value="high">Important</option>
            </select>
          </Field>
        </div>
        <p className="flex items-center gap-2 text-xs text-zinc-500"><WhatsappLogo size={14} /> Recipients are queued for WhatsApp delivery.</p>
        <button className="btn-primary w-full" data-testid="notice-submit-btn">{t("Post notice")}</button>
      </form>
    </Modal>
  );
}

export default function Notices() {
  const { user } = useAuth();
  const { t } = useT();
  const staff = ["admin", "teacher"].includes(user.role);
  const { data, loading, reload } = useApi("/notices");
  const [form, setForm] = useState(false);
  const del = async (n) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await api.delete(`/notices/${n.id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Circulars" title={t("Notices")}
        actions={staff && <>{user.role === "admin" && <ExportButtons entity="notices" />}<button className="btn-primary" onClick={() => setForm(true)} data-testid="post-notice-btn"><Plus size={16} weight="bold" /> {t("Post notice")}</button></>} />
      {loading ? <Loader /> : !data?.length ? <GlassCard><Empty /></GlassCard> : (
        <div className="relative space-y-5 before:absolute before:left-5 before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:from-blue-500/50 before:to-transparent">
          {data.map((n, i) => (
            <motion.div key={n.id} {...fadeUp(i)} className="relative pl-14">
              <span className={`absolute left-0 top-5 grid h-10 w-10 place-items-center rounded-2xl border ${n.priority === "high" ? "bg-rose-500/15 border-rose-500/30 text-rose-300" : "bg-blue-500/15 border-blue-500/30 text-blue-300"}`}>
                <Megaphone size={18} weight="duotone" />
              </span>
              <GlassCard hover className="p-6" data-testid={`notice-card-${n.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {n.priority === "high" && <Badge status="high">Important</Badge>}
                  <Badge status="queued">{t(AUD.find((a) => a[0] === n.audience)?.[1] || n.audience)}</Badge>
                  <span className="text-xs text-zinc-500">{fmtDate(n.created_at)} · {n.created_by_name}</span>
                  {staff && (user.role === "admin" || n.created_by === user.id) && (
                    <button className="btn-ghost !p-2 !text-rose-300 ml-auto" onClick={() => del(n)} data-testid={`notice-delete-${n.id}`}><Trash size={15} /></button>
                  )}
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold text-white">{n.title}</h3>
                <p className="mt-2 text-sm text-zinc-300 whitespace-pre-line leading-relaxed">{n.body}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      )}
      {form && <NoticeForm onClose={() => setForm(false)} onSaved={() => { setForm(false); reload(); }} />}
    </div>
  );
}
